import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { vehiclesApi, dashboardApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Car,
  Fuel,
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Upload,
  Image as ImageIcon,
  FileText,
  Trash2,
  Download,
  Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const DOC_TYPES = ['RC', 'Insurance', 'Permit', 'Fitness Certificate', 'PUC', 'Road Tax', 'Other'];

const VehicleDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('RC');
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [vehicleRes, profitRes] = await Promise.all([
        vehiclesApi.getById(id),
        dashboardApi.getVehicleProfitability(id),
      ]);
      setVehicle(vehicleRes.data);
      setProfitability(profitRes.data);
    } catch (error) {
      toast.error('Failed to fetch vehicle details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await vehiclesApi.uploadImage(id, formData);
      toast.success('Image uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await vehiclesApi.uploadDocument(id, docType, formData);
      toast.success('Document uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await vehiclesApi.deleteImage(id, imageId);
      toast.success('Image deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await vehiclesApi.deleteDocument(id, docId);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return (
      <Layout title="Vehicle Details">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!vehicle) {
    return (
      <Layout title="Vehicle Not Found">
        <div className="text-center py-12">
          <Car className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Vehicle not found</p>
          <Link to="/vehicles">
            <Button className="mt-4">Back to Vehicles</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const expenses = profitability?.expenses || {};
  const profit = profitability?.profitability || {};

  const getExpiryStatus = (dateStr) => {
    if (!dateStr) return { status: 'unknown', label: 'Not Set', color: 'text-slate-500' };
    const expiry = new Date(dateStr);
    const today = new Date();
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { status: 'expired', label: 'Expired', color: 'text-red-500' };
    if (daysLeft <= 7) return { status: 'critical', label: `${daysLeft} days`, color: 'text-red-500' };
    if (daysLeft <= 30) return { status: 'warning', label: `${daysLeft} days`, color: 'text-yellow-500' };
    return { status: 'valid', label: `${daysLeft} days`, color: 'text-emerald-500' };
  };

  return (
    <Layout
      title={vehicle.registration_number}
      subtitle={`${vehicle.make} ${vehicle.model} (${vehicle.year})`}
      actions={
        <Link to="/vehicles">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Vehicles
          </Button>
        </Link>
      }
    >
      <div className="space-y-6" data-testid="vehicle-detail-page">
        {/* Vehicle Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center">
                  <Car className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vehicle Type</p>
                  <p className="font-semibold capitalize">{vehicle.vehicle_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Fuel className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fuel Type</p>
                  <p className="font-semibold uppercase">{vehicle.fuel_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Purchase Price</p>
                  <p className="font-semibold tabular-nums">{formatCurrency(vehicle.purchase_price)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={`capitalize ${vehicle.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                    {vehicle.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Images & Documents Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vehicle Images */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
                Vehicle Images
              </CardTitle>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  ref={imageInputRef}
                  className="hidden"
                  data-testid="vehicle-image-input"
                />
                <Button 
                  size="sm" 
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="upload-vehicle-image-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Image
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {vehicle.images && vehicle.images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {vehicle.images.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={`${BACKEND_URL}${img.url}`}
                        alt={img.name}
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        <a href={`${BACKEND_URL}${img.url}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="secondary">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteImage(img.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 truncate">{img.name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No images uploaded</p>
                  <p className="text-sm">Upload vehicle photos for reference</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vehicle Documents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Vehicle Documents
              </CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    size="sm"
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                    data-testid="upload-vehicle-doc-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={docType} onValueChange={setDocType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DOC_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>File</Label>
                      <Input
                        type="file"
                        onChange={handleDocumentUpload}
                        ref={docInputRef}
                        data-testid="vehicle-doc-input"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {vehicle.documents && vehicle.documents.length > 0 ? (
                <div className="space-y-3">
                  {vehicle.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <Badge variant="outline" className="text-xs">{doc.doc_type}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={`${BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No documents uploaded</p>
                  <p className="text-sm">Upload RC, Insurance, Permit, etc.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Profitability & Expenses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                Profitability Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Gross Revenue</p>
                  <p className="text-xl font-bold text-emerald-600 tabular-nums">
                    {formatCurrency(profit.gross_revenue)}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Net Profit</p>
                  <p className={`text-xl font-bold tabular-nums ${profit.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(profit.net_profit)}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Revenue per KM</span>
                    <span className="font-medium">₹{profit.revenue_per_km || 0}</span>
                  </div>
                  <Progress value={Math.min((profit.revenue_per_km || 0) * 5, 100)} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Cost per KM</span>
                    <span className="font-medium">₹{profit.cost_per_km || 0}</span>
                  </div>
                  <Progress value={Math.min((profit.cost_per_km || 0) * 5, 100)} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Profit per KM</span>
                    <span className={`font-medium ${profit.profit_per_km >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ₹{profit.profit_per_km || 0}
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(Math.abs(profit.profit_per_km || 0) * 10, 100)} 
                    className={`h-2 ${profit.profit_per_km >= 0 ? '' : '[&>div]:bg-red-500'}`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <IndianRupee className="w-5 h-5 text-[#D4AF37]" />
                Expense Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <p className="text-sm text-slate-500">CAPEX</p>
                  <p className="text-lg font-bold text-blue-600 tabular-nums">
                    {formatCurrency(expenses.capex)}
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-xl text-center">
                  <p className="text-sm text-slate-500">OPEX</p>
                  <p className="text-lg font-bold text-orange-600 tabular-nums">
                    {formatCurrency(expenses.opex)}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl text-center">
                  <p className="text-sm text-slate-500">Depreciation</p>
                  <p className="text-lg font-bold text-purple-600 tabular-nums">
                    {formatCurrency(expenses.depreciation)}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-2">Total Expenses</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {formatCurrency((expenses.capex || 0) + (expenses.opex || 0) + (expenses.depreciation || 0))}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Compliance Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <AlertTriangle className="w-5 h-5 text-[#D4AF37]" />
              Compliance Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Insurance', date: vehicle.insurance_expiry },
                { label: 'Permit', date: vehicle.permit_expiry },
                { label: 'Fitness', date: vehicle.fitness_expiry },
                { label: 'PUC', date: vehicle.puc_expiry },
              ].map((item) => {
                const status = getExpiryStatus(item.date);
                return (
                  <div key={item.label} className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-500">{item.label}</p>
                      {status.status === 'valid' ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : status.status === 'expired' || status.status === 'critical' ? (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      ) : status.status === 'warning' ? (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      ) : null}
                    </div>
                    <p className="font-medium">{item.date || 'Not Set'}</p>
                    <p className={`text-sm ${status.color}`}>{status.label}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VehicleDetail;
